import "./AppointmentsPage.module.css";

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Calendar, CalendarDays } from "lucide-react";

import { AppointmentsList } from "components/AppointmentsList/AppointmentsList";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";

const ALL_TAB_OPTIONS = [
  {
    value: "appointments",
    label: "Appointments",
    icon: <CalendarDays size={16} strokeWidth={2} />,
  },
  {
    value: "my-appointments",
    label: "My Appointments",
    icon: <Calendar size={16} strokeWidth={2} />,
  },
];

const ACTIVE_TAB_STORAGE_KEY = "kinesin-appointments-active-tab";

const AppointmentsPage = () => {
  const [activeTab, setActiveTab] = useState("appointments");
  const { data: staffMember } = useStaffInfo();
  const location = useLocation();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);

  const rosterValue = staffMember?.staffMember?.roster ?? staffMember?.roster;
  const hasRoster = rosterValue === true;

  const tabOptions = useMemo(() => {
    if (hasRoster) {
      return ALL_TAB_OPTIONS;
    }

    return ALL_TAB_OPTIONS.filter((tab) => tab.value !== "my-appointments");
  }, [hasRoster]);

  // Initialize tab from storage, but only if valid for current user
  useEffect(() => {
    try {
      const stored = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
      const validValues = tabOptions.map((t) => t.value);

      if (validValues.includes(stored)) {
        setActiveTab(stored);
      } else {
        setActiveTab("appointments");
      }
    } catch {
      setActiveTab("appointments");
    }
  }, [tabOptions]);

  useEffect(() => {
    if (location.state?.action === "add") {
      setShowAddModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
    } catch {
      // Ignore storage errors — the tab just won't be remembered next time.
    }
  }, [activeTab]);

  // Only show toggle if user has roster (multiple tabs available)
  const tabToggle = hasRoster ? (
    <TabSwitcher
      size="small"
      options={tabOptions}
      value={activeTab}
      onChange={setActiveTab}
    />
  ) : null;

  // If user doesn't have roster, always show all appointments regardless of activeTab
  const effectiveTab = hasRoster ? activeTab : "appointments";

  return (
    <div>
      {effectiveTab === "appointments" && (
        <AppointmentsList
          filterOutOfRosterDoctors
          allowMonthView
          extraControls={tabToggle}
          showAddAppointmentModal={showAddModal}
          setShowAddAppointmentModal={setShowAddModal}
        />
      )}
      {effectiveTab === "my-appointments" && (
        <AppointmentsList
          hideTimeIndicator
          doctorId={staffMember?.staffMember?.id}
          multiDay={true}
          allowMonthView
          extraControls={tabToggle}
          showAddAppointmentModal={showAddModal}
          setShowAddAppointmentModal={setShowAddModal}
        />
      )}
    </div>
  );
};

export { AppointmentsPage };
